import * as XLSX from 'xlsx';
import { format, parse } from 'date-fns';
import { SurveyAnswerRow, SurveyLaunch } from '../types/survey';
import { saveWorkbook } from './exportUtils';
import {
  getEnabledTextQuestions,
  getQuestionLabel,
  getRatingQuestion,
  localeLabel,
  SURVEY_RATING_MAX,
  SURVEY_RATING_MIN,
  textAccessor,
} from '../pages/Surveys/surveyUtils';

const SURVEY_SHEET_NAME = 'Respuestas';
const COLUMN_WIDTH = 24;

export function formatSurveyDay(day: string): string {
  return format(parse(day, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy');
}

interface SurveyExportColumn {
  header: string;
  value: (row: SurveyAnswerRow) => string | number;
}

/**
 * Ronda, Fecha, Idioma (D-A15, only for an organization with more than one language), then only the questions of
 * the round (its own snapshot), in order.
 */
function getSurveyExportColumns(launch: SurveyLaunch, locales: string[]): SurveyExportColumn[] {
  const textColumns = getEnabledTextQuestions(launch.questions).map(
    (question, index): SurveyExportColumn => ({
      header: getQuestionLabel(question, `Pregunta ${index + 1}`),
      value: (row) => row[textAccessor(question.slot)] ?? '',
    }),
  );
  const rating = getRatingQuestion(launch.questions);
  const ratingColumns: SurveyExportColumn[] = rating
    ? [
        {
          header: `${getQuestionLabel(rating, 'Valoración')} (${SURVEY_RATING_MIN}-${SURVEY_RATING_MAX})`,
          value: (row) => row.rating ?? '',
        },
      ]
    : [];

  return [
    { header: 'Ronda', value: (row) => row.launchNumber },
    { header: 'Fecha', value: (row) => formatSurveyDay(row.day) },
    ...(locales.length > 1 ? [{ header: 'Idioma', value: (row: SurveyAnswerRow) => localeLabel(row.locale) }] : []),
    ...textColumns,
    ...ratingColumns,
  ];
}

export function getSurveyExportHeaders(launch: SurveyLaunch, locales: string[]): string[] {
  return getSurveyExportColumns(launch, locales).map((column) => column.header);
}

function toTextCell(value: string): XLSX.CellObject {
  return { t: 's', v: value, z: '@' };
}

export function buildSurveyAnswersWorksheet(rows: SurveyAnswerRow[], launch: SurveyLaunch, locales: string[]): XLSX.WorkSheet {
  const columns = getSurveyExportColumns(launch, locales);
  const headers = getSurveyExportHeaders(launch, locales);
  const body = rows.map((row) => columns.map((column) => column.value(row)));
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body]);

  Object.keys(worksheet)
    .filter((address) => !address.startsWith('!'))
    .forEach((address) => {
      const cell = worksheet[address] as XLSX.CellObject;
      // aoa_to_sheet types every string (also an empty one) as 's'
      if (cell.t === 's') worksheet[address] = toTextCell(String(cell.v));
    });

  worksheet['!cols'] = headers.map(() => ({ wch: COLUMN_WIDTH }));
  return worksheet;
}

export async function exportSurveyAnswersToExcel(
  rows: SurveyAnswerRow[],
  launch: SurveyLaunch,
  fileName: string,
  locales: string[],
): Promise<void> {
  const worksheet = buildSurveyAnswersWorksheet(rows, launch, locales);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, SURVEY_SHEET_NAME);
  await saveWorkbook(workbook, fileName);
}
